import {
  DEFAULT_FILTER_PRIORITY,
  AvatarType,
  type AnyBlock,
  type MutableFilterBlock,
  type FilterQuery,
  type IndexedBlockValueItem,
  type FilterKey,
  type SuppliedFilterProviderOptions,
} from '@github-ui/filter'
import {BaseUserFilterProvider, type User} from '@github-ui/filter/providers'
import {getUnescapedFilterValue} from '@github-ui/filter/utils'
import {PersonIcon} from '@primer/octicons-react'
import type {User as ActorUser} from '@github-ui/user-selector'

export interface ActorFilterProviderConfig {
  displayName: string
  key: string
  description: string
  /** Pre-loaded actors passed from a hook like useBypassActors or useInsightsActors */
  actors: ActorUser[]
}

/** Converts raw API user format to internal User format */
function toUser(actor: ActorUser): User {
  return {
    login: actor.login,
    name: actor.name ?? actor.login,
    avatarUrl: actor.primaryAvatarUrl,
  }
}

export class ActorFilterProvider extends BaseUserFilterProvider {
  #actors: User[]

  constructor(config: ActorFilterProviderConfig, options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: config.displayName,
      key: config.key,
      icon: PersonIcon,
      description: config.description,
      priority: DEFAULT_FILTER_PRIORITY,
    }

    super(filterKey, {showAtMe: false}, options)
    this.#actors = config.actors.map(toUser)
  }

  override async fetchSuggestions(
    value: string,
    _block: AnyBlock | MutableFilterBlock,
    _filterQuery: FilterQuery,
  ): Promise<User[] | null> {
    if (!value) {
      return this.#actors
    }

    const lowerValue = value.toLowerCase()
    return this.#actors.filter(
      actor => actor.login.toLowerCase().includes(lowerValue) || (actor.name ?? '').toLowerCase().includes(lowerValue),
    )
  }

  override validateValue(
    _filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    _user: User | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!extractedValue) return false

    const actor = this.#actors.find(a => a.login.toLowerCase() === extractedValue.toLowerCase())

    if (actor) {
      return {
        avatar: actor.avatarUrl ? {url: actor.avatarUrl, type: AvatarType.User} : undefined,
        value: extractedValue,
        displayName: actor.name,
      }
    }

    // Accept value if actors list is empty (actors may not have loaded yet)
    if (this.#actors.length === 0) {
      return {value: extractedValue}
    }

    return false
  }
}
